/*
    Quanto può permettersi lo studio animato il dispositivo del display. La scena (Scene.js) pesa quasi tutta sul
    compositing: decine di livelli grandi quanto lo schermo, sfocature, maschere. Con una GPU non costa nulla; senza
    accelerazione hardware o con una GPU debole scende a 15-30 fps e si porta dietro tutta l'interfaccia. Allora Stage
    mostra al suo posto stage.jpg, un fotogramma fermo della stessa scena.

    - Prima di montare la scena (controlli che costano poco): preferenza di sistema "riduci movimento", memoria del
      dispositivo fino a 2 GB (solo i browser Chromium la dicono), nessuna accelerazione hardware.
    - Sul campo: dopo ogni cutscene, quando la scena è finalmente visibile, si contano i fotogrammi. requestAnimationFrame
      rallenta anche quando il collo di bottiglia è la GPU, non solo quando lo è il thread principale. Durante il video
      invece non si può misurare: il browser non disegna ciò che sta sotto un livello opaco e la scena sembrerebbe leggera.
    Il verdetto "troppo lenta" resta in memoria per LITE_DAYS giorni, poi si riprova.

    La modalità si sceglie anche a mano, dal toggle "Modalità lite" delle impostazioni (SettingsDialog), solo per questo
    dispositivo: la scelta vale finché non la si cambia, ferma le misure e Stage la applica subito, anche in partita.
    Tutto sta in localStorage.stage, di cui il toggle è lo specchio: "lite" o "full" (scelta a mano), oppure il momento
    del verdetto automatico (vale LITE_DAYS giorni), oppure niente (ancora da misurare).
*/

const KEY = 'stage';
const LITE_DAYS = 7;
const FPS_MIN = 40;         //sotto questa cadenza la scena lascia il posto all'immagine
const SCREEN_SHARE = .7;    //con uno schermo lento (30 Hz, risparmio energetico) basta il 70% della sua frequenza
const VEIL_COST = .85;      //il velo sfocato toglie il 15-25% dei fotogrammi (misurato in compositing software)

const read = () => {
    try {
        return window.localStorage.getItem(KEY);
    } catch (e) {
        return null;
    }
};

const write = (value) => {
    try {
        window.localStorage.setItem(KEY, value);
    } catch (e) {}
};

//chi mostra la modalità (Stage e il toggle) viene avvisato a ogni cambio: localStorage non lo fa nella stessa pagina.
//manual: scelta dal toggle, da applicare subito
const listeners = new Set();
const notify = (manual) => listeners.forEach((listener) => listener(storedQuality(), manual));
export function onQualityChange(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

//nessuna accelerazione hardware: un contesto WebGL con failIfMajorPerformanceCaveat non nasce quando il browser
//disegnerebbe in software, uno normale sì. Se non nasce nemmeno quello, WebGL è spento (estensioni, criteri aziendali)
//e non se ne ricava nulla. Si controlla una volta per sessione (40-100 ms)
let software = null;
export function softwareRendering() {
    if (software !== null) return software;
    software = false;
    const drop = (gl) => gl.getExtension('WEBGL_lose_context')?.loseContext();
    try {
        const fast = document.createElement('canvas').getContext('webgl', { failIfMajorPerformanceCaveat: true });
        if (fast) {
            drop(fast);
        } else {
            const slow = document.createElement('canvas').getContext('webgl');
            if (slow) {
                drop(slow);
                software = true;
            }
        }
    } catch (e) {}
    return software;
}

//"lite": solo l'immagine (scelta a mano o verdetto recente); "full": la scena, senza misure (scelta a mano);
//"auto": la scena, da misurare
export function storedQuality() {
    const saved = read();
    if (saved === 'lite' || saved === 'full') return saved;
    return Date.now() - Number(saved) < LITE_DAYS * 864e5 ? 'lite' : 'auto';
}

//valore del toggle nelle impostazioni
export const isLite = () => storedQuality() === 'lite';

//scelta a mano dal toggle
export function chooseLite(on) {
    write(on ? 'lite' : 'full');
    notify(true);
}

//verdetto automatico: dai controlli iniziali o dalla misura sul campo
export function rememberLite() {
    write(String(Date.now()));
    notify(false);
}

//dispositivo debole già a priori: "riduci movimento", memoria fino a 2 GB, nessuna accelerazione hardware
export function weakDevice() {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        || (navigator.deviceMemory > 0 && navigator.deviceMemory <= 2)
        || softwareRendering();
}

//intervalli tra un fotogramma e l'altro per ms millisecondi, in ordine crescente; null se la pagina viene nascosta
//(requestAnimationFrame si ferma) o se i fotogrammi sono troppo pochi per dire qualcosa
export function measureFrames(ms) {
    return new Promise((resolve) => {
        if (document.hidden) return resolve(null);
        const gaps = [];
        let last = 0, end = 0, frame = 0;
        const done = (result) => {
            cancelAnimationFrame(frame);
            clearTimeout(guard);
            document.removeEventListener('visibilitychange', hidden);
            resolve(result);
        };
        const hidden = () => done(null);
        const tick = (now) => {
            if (last) gaps.push(now - last);
            else end = now + ms;
            last = now;
            if (now < end) frame = requestAnimationFrame(tick);
            else done(gaps.length < 2 ? null : gaps.sort((a, b) => a - b));
        };
        //se i fotogrammi non arrivano proprio (pagina congelata) non si resta appesi
        const guard = setTimeout(() => done(null), ms + 3000);
        document.addEventListener('visibilitychange', hidden);
        frame = requestAnimationFrame(tick);
    });
}

//fotogrammi al secondo. Il 10% degli intervalli più lunghi non conta: sono i lavori del gioco (render di React,
//caricamenti), non il costo della scena
export function fps(gaps) {
    const kept = gaps.length >= 10 ? gaps.slice(0, Math.ceil(gaps.length * .9)) : gaps;
    return 1000 * kept.length / kept.reduce((a, b) => a + b, 0);
}

//frequenza dello schermo, misurata una volta per sessione prima di montare la scena: il quarto degli intervalli più
//brevi, quelli dei fotogrammi arrivati in tempo, anche se qualche lavoro del gioco ne fa saltare altri
let screen = null;
export function screenRate() {
    if (!screen) {
        screen = measureFrames(500).then((gaps) => {
            if (!gaps) screen = null;   //pagina nascosta: si riprova la prossima volta
            return gaps ? 1000 / gaps[Math.floor(gaps.length / 4)] : 60;
        });
    }
    return screen;
}

//troppo lenta: sotto FPS_MIN fotogrammi al secondo, o sotto il 70% della frequenza dello schermo se questo è più lento.
//Sotto il velo il limite scende di quanto costa il velo
export function tooSlow(rate, screenHz, veiled) {
    return rate < Math.min(FPS_MIN, screenHz * SCREEN_SHARE) * (veiled ? VEIL_COST : 1);
}
